import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "hooks/reduxHooks";
import { doLogin } from "store/slices/auth.slice";
import { notificationController } from "components/Common/Notification/Notification";
import websiteLogo from "../../images/logo.png";
import Button from "components/Common/Button";
import { Divider, Flex } from "antd";
import SSOLogin from "./SSOGoogle";
import SSOMicrosoftLogin from "./SSOMicrosoft";
import { FaEye, FaEyeSlash } from "react-icons/fa";
const FaEyeSin = FaEye as unknown as React.FC as any;
const FaEyeSlashSin = FaEyeSlash as unknown as React.FC as any;
import AuthFooter from "components/Footer/AuthFooter";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role');

  const [isLoading, setLoading] = useState(false);
  const initialValues = {
    email: "",
    password: "",
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validationSchema = Yup.object({
    // eslint-disable-next-line newline-per-chained-call
    email: Yup.string().email("Invalid email address").required("Required"),
    password: Yup.string().required("Required"),
  });

  const handleSubmit = (values: { email: string; password: string }) => {
    // eslint-disable-next-line no-debugger

    setLoading(true);
    dispatch(
      doLogin({
        email: values?.email,
        password: values?.password,
      })
    )
      .unwrap()
      .then((data: any) => {
        data.user.role === "EXTERNAL_CONTRIBUTOR"
          ? navigate("/user-project-list?page=1")
          : navigate("/");
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
        setLoading(false);
      });
  };

  return (
    <div className="flex min-h-full flex-col justify-center px-6 pb-0 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img className="mx-auto h-10 w-auto" src={websiteLogo} alt="Your Company" />
        <h2 className="mt-7 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Form className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                Email address
              </label>
              <div className="mt-2">
                <Field
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="p-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password
                </label>
                <div className="text-sm">
                  {/* <a href="#" className="font-semibold text-primary">
                    Forgot password?
                  </a> */}
                  <Link to={"/auth/forgot-password"} className="font-semibold text-primary">
                    Forgot password?
                  </Link>
                </div>
              </div>
              <div className="mt-2 relative">
                {/* Input and Eye icon wrapper */}
                <div className="relative">
                  <Field
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="p-2 pr-10 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  {/* Eye Icon */}
                  <div
                    className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <FaEyeSlashSin className="text-gray-500" />
                    ) : (
                      <FaEyeSin className="text-gray-500" />
                    )}
                  </div>
                </div>

                {/* Error Message placed below */}
                <ErrorMessage
                  name="password"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>
            </div>

            <Flex vertical gap={12}>
              <Button
                type="submit"
                isLoading={isLoading}
                variant="primary"
                className="bg-primary text-white px-3 py-2 rounded-md w-[100%]"
              >
                Sign In
              </Button>
             {role && <Button
                disabled={isLoading}
                onClick={() => navigate("/auth/signup")}
                variant="secondary"
                className="px-3 py-2 border rounded-md w-[100%]"
              >
                Sign Up
              </Button>}
            </Flex>
            <Divider>OR</Divider>
            <Flex justify="center" className="w-full items-center" vertical>
              <SSOLogin></SSOLogin>
              <SSOMicrosoftLogin></SSOMicrosoftLogin>
            </Flex>
          </Form>
        </Formik>
        <AuthFooter />
        {/* <p className="mt-10 text-center text-sm text-gray-500">
          Not a member?
          <a href="#" className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500">
            Start a 14 day free trial
          </a>
        </p> */}
      </div>
    </div>
  );
};

export default Login;
